import React, { memo } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import AddBoxOutlined from '@mui/icons-material/AddBoxOutlined';
import Inventory from '@mui/icons-material/Inventory2Outlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import { alpha } from '@mui/material/styles';
import FormCard from './FormCard';
import PackageCard, { PackageCardHandlers } from './PackageCard';
import { PackageDraft, allItems, plural } from './labelForm';

const multiParcelBadge = (
  <Box
    component="span"
    sx={(theme) => ({
      height: 26,
      pl: 1,
      pr: 1.25,
      borderRadius: 13,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontSize: 12,
      fontWeight: 600,
      color: 'info.main',
      bgcolor: alpha(theme.palette.info.main, 0.1),
    })}
  >
    <LayersOutlined sx={{ fontSize: 16 }} />
    Multi-parcel shipment
  </Box>
);

interface PackagesCardProps extends PackageCardHandlers {
  packages: PackageDraft[];
  onAdd: () => void;
}

/** Packages section: one card per package (each gets its own label), items nested inside. */
const PackagesCard: React.FC<PackagesCardProps> = ({ packages, onAdd, ...handlers }) => {
  const items = allItems(packages);
  const hazmatCount = items.filter((item) => item.hazmat).length;
  const multiParcel = packages.length > 1;

  return (
    <FormCard
      icon={<Inventory />}
      title="Packages"
      summary={`${plural(packages.length, 'package')} · ${plural(items.length, 'item')}${hazmatCount ? ` · ${hazmatCount} hazmat` : ''}`}
      actions={multiParcel ? multiParcelBadge : undefined}
    >
      <Typography sx={{ mt: -1, ml: { sm: '34px' }, fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
        Each package gets its own label. Add the order items packed inside each one — every package needs at least one item.
      </Typography>

      {packages.map((pkg, index) => (
        <PackageCard key={pkg.key} pkg={pkg} index={index} total={packages.length} removable={multiParcel} {...handlers} />
      ))}

      <ButtonBase
        onClick={onAdd}
        sx={{
          height: 52,
          gap: 1,
          borderRadius: 3.5,
          border: '1.5px dashed',
          borderColor: 'divider',
          color: 'primary.dark',
          fontSize: 14,
          fontWeight: 600,
          '& svg': { fontSize: 20 },
          '&:hover': { bgcolor: 'brand.container', borderColor: 'primary.main' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        <AddBoxOutlined />
        Add another package
      </ButtonBase>
    </FormCard>
  );
};

export default memo(PackagesCard);
